import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Shield,
  User,
  Trash2,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  Lock,
  ArrowUpRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { format, parseISO } from "date-fns";

const AdminManagement = () => {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [allowedAdmins, setAllowedAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [showCancelInviteDialog, setShowCancelInviteDialog] = useState(false);
  const [inviteToCancel, setInviteToCancel] = useState(null);
  const [formData, setFormData] = useState({ email: "", name: "", role: "coordinator" });
  const [changeRequests, setChangeRequests] = useState([]);
  const [planId, setPlanId] = useState("starter");

  const canInviteUsers = planId === "team" || planId === "enterprise";

  const fetchData = useCallback(async () => {
    try {
      const [adminsRes, allowedRes, crRes, subRes] = await Promise.all([
        axios.get(`${API}/admins`, { withCredentials: true }),
        axios.get(`${API}/allowed-admins`, { withCredentials: true }),
        axios.get(`${API}/change-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/subscription/current`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setAdmins(adminsRes.data);
      setAllowedAdmins(allowedRes.data);
      setChangeRequests(crRes.data);
      setPlanId(subRes.data?.plan_id || "starter");
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admins`, formData, { withCredentials: true });
      if (res.data.email_sent) {
        toast.success(`Invite sent to ${formData.email}`);
      } else {
        toast.warning(res.data.message || `Invite created for ${formData.email}, but the email could not be sent.`);
      }
      setIsModalOpen(false);
      setFormData({ email: "", name: "", role: "coordinator" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite admin");
    }
  };

  const handleRemoveAdmin = async () => {
    if (!adminToDelete) return;
    try {
      await axios.delete(`${API}/admins/${adminToDelete.user_id}`, { withCredentials: true });
      toast.success("Admin removed");
      setShowDeleteDialog(false);
      setAdminToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove admin");
    }
  };

  const handleCancelInvite = async () => {
    if (!inviteToCancel) return;
    try {
      await axios.delete(`${API}/allowed-admins/${encodeURIComponent(inviteToCancel.email)}`, { withCredentials: true });
      toast.success(`Invite cancelled for ${inviteToCancel.email}`);
      setShowCancelInviteDialog(false);
      setInviteToCancel(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel invite");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleChangeRequest = async (requestId, action) => {
    try {
      await axios.post(`${API}/change-requests/${requestId}/${action}`, {}, { withCredentials: true });
      toast.success(`Request ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} request`);
    }
  };

  // Check if an allowed email has registered
  const isRegistered = useCallback((email) => {
    return admins.some(a => a.email === email);
  }, [admins]);

  const activeAdmins = useMemo(() => admins.filter(a => a.is_active !== false), [admins]);
  const pendingInvites = useMemo(() => allowedAdmins.filter(a => !isRegistered(a.email)), [allowedAdmins, isRegistered]);

  if (user?.role !== "super_admin") {
    return (
      <div className="p-6 md:p-8">
        <Card className="card-surface">
          <CardContent className="py-12">
            <div className="empty-state">
              <Shield className="empty-state-icon text-red-300" />
              <p className="empty-state-title">Access Denied</p>
              <p className="empty-state-description">
                Only admins can access this page.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">
              Users
            </h1>
            <p className="text-[var(--t-text-muted)] mt-1">Manage users who can access the system</p>
          </div>
          {canInviteUsers ? (
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="btn-primary"
              data-testid="invite-admin-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Invite User
            </Button>
          ) : (
            <Button
              onClick={() => navigate("/settings")}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="upgrade-to-invite-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Upgrade to Invite Users
              <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Upgrade Banner for Starter/Pro */}
      {!canInviteUsers && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="upgrade-banner">
          <Lock className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Team features require an upgrade</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Your current plan supports a single user. Upgrade to the <strong>Team plan</strong> to invite up to 5 team members, assign tasks, and access role-based permissions.
            </p>
            <Button
              variant="link"
              className="text-amber-700 hover:text-amber-900 p-0 h-auto mt-1 text-xs font-semibold"
              onClick={() => navigate("/settings")}
              data-testid="upgrade-link"
            >
              View Plans & Upgrade <ArrowUpRight className="w-3 h-3 ml-0.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Active Admins */}
      <Card className="card-surface mb-6" data-testid="active-admins-card">
        <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
          <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
            Active Admins
          </CardTitle>
        </CardHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="table-header">Name</TableHead>
              <TableHead className="table-header">Email</TableHead>
              <TableHead className="table-header">Role</TableHead>
              <TableHead className="table-header">Status</TableHead>
              <TableHead className="table-header">Joined</TableHead>
              <TableHead className="table-header text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeAdmins.map((admin, index) => (
              <TableRow key={admin.user_id} data-testid={`admin-row-${index}`}>
                <TableCell className="table-cell">
                  <div className="flex items-center gap-3">
                    {admin.picture ? (
                      <img src={admin.picture} alt={admin.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[var(--t-primary)] flex items-center justify-center text-white text-sm font-medium">
                        {admin.name?.charAt(0)}
                      </div>
                    )}
                    <span className="font-medium">{admin.name}</span>
                  </div>
                </TableCell>
                <TableCell className="table-cell">
                  <a href={`mailto:${admin.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {admin.email}
                  </a>
                </TableCell>
                <TableCell className="table-cell">
                  {admin.user_id === user?.user_id ? (
                    <Badge className="bg-[var(--t-primary)]/10 text-[var(--t-primary)]">Admin</Badge>
                  ) : (
                    <Select
                      value={admin.role === "super_admin" ? "super_admin" : "coordinator"}
                      onValueChange={(v) => handleRoleChange(admin.user_id, v)}
                    >
                      <SelectTrigger className="h-7 w-32 text-xs" data-testid={`role-select-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_admin">Admin</SelectItem>
                        <SelectItem value="coordinator">Coordinator</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="table-cell">
                  <Badge className="bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                    <CheckCircle className="w-3 h-3" />
                    Active
                  </Badge>
                </TableCell>
                <TableCell className="table-cell text-[var(--t-text-muted)]">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {admin.created_at ? format(parseISO(admin.created_at), 'MMM d, yyyy') : '-'}
                  </div>
                </TableCell>
                <TableCell className="table-cell text-right">
                  {admin.user_id !== user?.user_id && (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[var(--t-primary)] hover:bg-[var(--t-primary)]/10"
                        onClick={async () => {
                          const ok = await startImpersonation(admin.user_id);
                          if (ok) navigate("/dashboard");
                        }}
                        data-testid={`impersonate-${index}`}
                        title={`View as ${admin.name}`}
                      >
                        <UserCheck className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => { setAdminToDelete(admin); setShowDeleteDialog(true); }}
                        data-testid={`remove-admin-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <Card className="card-surface" data-testid="pending-invites-card">
          <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
            <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
              Pending Invites
            </CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="table-header">Name</TableHead>
                <TableHead className="table-header">Email</TableHead>
                <TableHead className="table-header">Role</TableHead>
                <TableHead className="table-header">Status</TableHead>
                <TableHead className="table-header">Invited</TableHead>
                <TableHead className="table-header text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingInvites.map((invite, index) => (
                <TableRow key={invite.email} data-testid={`invite-row-${index}`}>
                  <TableCell className="table-cell font-medium">{invite.name}</TableCell>
                  <TableCell className="table-cell">
                    <span className="flex items-center gap-1 text-[var(--t-text-muted)]">
                      <Mail className="w-3 h-3" />
                      {invite.email}
                    </span>
                  </TableCell>
                  <TableCell className="table-cell">
                    <Badge className={invite.role === "super_admin" ? "bg-[var(--t-primary)]/10 text-[var(--t-primary)]" : "bg-[var(--t-text)]/10 text-[var(--t-text)]"}>
                      {invite.role === "super_admin" ? "Admin" : "Coordinator"}
                    </Badge>
                  </TableCell>
                  <TableCell className="table-cell">
                    <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1 w-fit">
                      <Clock className="w-3 h-3" />
                      Pending
                    </Badge>
                  </TableCell>
                  <TableCell className="table-cell text-[var(--t-text-muted)]">
                    {invite.added_at ? format(parseISO(invite.added_at), 'MMM d, yyyy') : '-'}
                  </TableCell>
                  <TableCell className="table-cell text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => { setInviteToCancel(invite); setShowCancelInviteDialog(true); }}
                      data-testid={`cancel-invite-${index}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Change Requests */}
      {changeRequests.length > 0 && (
        <Card className="card-surface" data-testid="change-requests-card">
          <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
            <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
              Pending Approvals
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-slate-100">
            {changeRequests.map((cr) => (
              <div key={cr.request_id} className="px-5 py-4 flex items-center gap-4" data-testid={`cr-${cr.request_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--t-text)]">
                    Workflow Edit: {cr.proposed_changes?.name || cr.template_id}
                  </p>
                  <p className="text-xs text-[var(--t-text-muted)]">
                    Requested by {cr.requested_by_name} ({cr.requested_by_email})
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleChangeRequest(cr.request_id, 'approve')} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${cr.request_id}`}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleChangeRequest(cr.request_id, 'reject')} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${cr.request_id}`}>
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Invite Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent data-testid="invite-admin-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold [font-family:var(--t-heading-font)]">
              Invite User
            </DialogTitle>
            <DialogDescription>
              Add a new user by their email. They'll be able to log in with Google using this email.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteAdmin} className="space-y-4">
            <div className="form-group">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="John Doe"
                data-testid="input-admin-name"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="john@example.com"
                data-testid="input-admin-email"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="role">Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger data-testid="select-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Admin</SelectItem>
                  <SelectItem value="coordinator">Coordinator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-invite">
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove Admin Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="remove-admin-dialog">
          <DialogHeader>
            <DialogTitle>Remove User</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{adminToDelete?.name}" ({adminToDelete?.email})? They will no longer be able to access the system.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveAdmin} data-testid="confirm-remove-admin">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invite Dialog */}
      <Dialog open={showCancelInviteDialog} onOpenChange={setShowCancelInviteDialog}>
        <DialogContent data-testid="cancel-invite-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Invite</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the invite for "{inviteToCancel?.name}" ({inviteToCancel?.email})? They will no longer be able to join this organization.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelInviteDialog(false)}>
              Keep Invite
            </Button>
            <Button variant="destructive" onClick={handleCancelInvite} data-testid="confirm-cancel-invite">
              Cancel Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
